import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AdminPageHeader, RowActions, ResponsiveTableWrap, AdminEmptyState } from "@/components/admin/crud-helpers";
import { MediaField } from "@/components/admin/media-picker";
import { FRONTEND_PAGES, type FrontendPageEntry } from "@/lib/frontend-pages-registry";
import type { PageOverride } from "@/hooks/use-page-override";
import { Download, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/frontend-pages")({
  component: FrontendPagesAdmin,
});

function FrontendPagesAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ entry: FrontendPageEntry; row: PageOverride | null } | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const { data: rows, isLoading } = useQuery({
    queryKey: ["page_overrides", "all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("page_overrides").select("*");
      if (error) throw error;
      return data as PageOverride[];
    },
  });

  const byPath = useMemo(() => {
    const m = new Map<string, PageOverride>();
    for (const r of rows ?? []) m.set(r.path, r);
    return m;
  }, [rows]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["page_overrides"] });
    qc.invalidateQueries({ queryKey: ["page_override"] });
  };

  const seedAll = useMutation({
    mutationFn: async () => {
      const payload = FRONTEND_PAGES.map((e) => ({
        path: e.path,
        label: e.label,
        category: e.category,
        seo_title: e.seo_title ?? null,
        seo_description: e.seo_description ?? null,
        hero_eyebrow: e.hero_eyebrow ?? null,
        hero_title: e.hero_title ?? null,
        hero_sub: e.hero_sub ?? null,
        enabled: true,
      }));
      const { error } = await supabase.from("page_overrides").upsert(payload, { onConflict: "path", ignoreDuplicates: true });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Defaults imported"); invalidate(); },
    onError: (e: any) => toast.error(e.message ?? "Import failed"),
  });

  const upsert = useMutation({
    mutationFn: async (values: any) => {
      const { error } = await supabase.from("page_overrides").upsert(values, { onConflict: "path" });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Saved"); invalidate(); setOpen(false); },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("page_overrides").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Override cleared"); invalidate(); },
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  const categories = useMemo(
    () => Array.from(new Set(FRONTEND_PAGES.map((p) => p.category))),
    [],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return FRONTEND_PAGES.filter((e) => {
      if (categoryFilter !== "all" && e.category !== categoryFilter) return false;
      if (!q) return true;
      return e.label.toLowerCase().includes(q) || e.path.toLowerCase().includes(q) || e.category.toLowerCase().includes(q);
    });
  }, [search, categoryFilter]);

  const importedCount = byPath.size;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Frontend pages"
        description="Edit SEO and hero content for every public page. Overrides apply live without redeploying."
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by page name, path or category…"
        count={filtered.length}
        countLabel={`of ${FRONTEND_PAGES.length}`}
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => seedAll.mutate()}
            disabled={seedAll.isPending}
          >
            <Download className="mr-1 h-4 w-4" />
            {seedAll.isPending ? "Importing…" : "Import defaults"}
          </Button>
        }
      />

      <div className="rounded-lg border border-border bg-card/40 px-3 py-2 text-xs text-muted-foreground">
        {importedCount} of {FRONTEND_PAGES.length} pages have custom overrides.
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant={categoryFilter === "all" ? "default" : "outline"} onClick={() => setCategoryFilter("all")}>
          All
        </Button>
        {categories.map((c) => (
          <Button key={c} size="sm" variant={categoryFilter === c ? "default" : "outline"} onClick={() => setCategoryFilter(c)}>
            {c}
          </Button>
        ))}
      </div>

      {!isLoading && !filtered.length ? (
        <AdminEmptyState title="No pages match" />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Page</TableHead>
                  <TableHead className="hidden md:table-cell">Path</TableHead>
                  <TableHead className="hidden sm:table-cell">Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {!isLoading && filtered.map((e) => {
                  const row = byPath.get(e.path) ?? null;
                  return (
                    <TableRow key={e.path}>
                      <TableCell className="font-medium">
                        <div className="truncate">{e.label}</div>
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground md:hidden">
                          <a href={e.path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
                            {e.path} <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        <a href={e.path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
                          {e.path}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell"><Badge variant="secondary">{e.category}</Badge></TableCell>
                      <TableCell>
                        {row ? (
                          <Badge variant={row.enabled ? "default" : "secondary"}>
                            {row.enabled ? "Override" : "Disabled"}
                          </Badge>
                        ) : (
                          <Badge variant="outline">Default</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <RowActions
                          onEdit={() => { setEditing({ entry: e, row }); setOpen(true); }}
                          onDelete={row ? () => { if (confirm(`Remove override for ${e.label}?`)) remove.mutate(row.id); } : undefined}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ResponsiveTableWrap>
        </div>
      )}

      {editing && (
        <PageOverrideDialog
          key={editing.entry.path}
          open={open}
          onOpenChange={setOpen}
          entry={editing.entry}
          row={editing.row}
          onSubmit={(values) => upsert.mutate(values)}
        />
      )}
    </div>
  );
}

function PageOverrideDialog({
  open, onOpenChange, entry, row, onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  entry: FrontendPageEntry;
  row: PageOverride | null;
  onSubmit: (values: any) => void;
}) {
  const [seoTitle, setSeoTitle] = useState(row?.seo_title ?? entry.seo_title ?? "");
  const [seoDesc, setSeoDesc] = useState(row?.seo_description ?? entry.seo_description ?? "");
  const [eyebrow, setEyebrow] = useState(row?.hero_eyebrow ?? entry.hero_eyebrow ?? "");
  const [title, setTitle] = useState(row?.hero_title ?? entry.hero_title ?? "");
  const [sub, setSub] = useState(row?.hero_sub ?? entry.hero_sub ?? "");
  const [cover, setCover] = useState(row?.cover_url ?? "");
  const [enabled, setEnabled] = useState(row?.enabled ?? true);

  const handleSubmit = () => {
    onSubmit({
      path: entry.path,
      label: entry.label,
      category: entry.category,
      seo_title: seoTitle || null,
      seo_description: seoDesc || null,
      hero_eyebrow: eyebrow || null,
      hero_title: title || null,
      hero_sub: sub || null,
      cover_url: cover || null,
      enabled,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit {entry.label} — <span className="font-mono text-sm text-muted-foreground">{entry.path}</span></DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>SEO title</Label>
            <Input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} maxLength={70} />
            <p className="text-xs text-muted-foreground">{seoTitle.length}/60 recommended</p>
          </div>
          <div className="grid gap-2">
            <Label>SEO description</Label>
            <Textarea rows={2} value={seoDesc} onChange={(e) => setSeoDesc(e.target.value)} maxLength={180} />
            <p className="text-xs text-muted-foreground">{seoDesc.length}/160 recommended</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>Hero eyebrow</Label>
              <Input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Hero title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Hero subtitle</Label>
            <Textarea rows={2} value={sub} onChange={(e) => setSub(e.target.value)} />
          </div>
          <MediaField label="Cover / OG image" value={cover} onChange={(url) => setCover(url ?? "")} />
          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <Label>Override enabled (off = fall back to code defaults)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
